import InnerHTML from 'dangerously-set-html-content';
import { PropTypes } from 'prop-types';
import React, {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import {
  useGotoRecoilSnapshot,
  useRecoilSnapshot,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from 'recoil';
import { EVENT_CODES, EVENTS } from './common/data/Constants';
import { checkHeartbeatStatus } from './common/fn/App';
import { getConfigVariable } from './common/fn/Config';
import { fpTrack } from './common/fn/FreshpaintUtils';
import { fetchHeartbeat } from './common/fn/Query';
import { apiVersion } from './common/recoil/atom/APIVersion';
import { dateSelectionVersion } from './common/recoil/atom/DateSelectionVersion';
import { LogAction } from './common/recoil/atom/LogAction';
import { queryString } from './common/recoil/atom/QueryString';
import LoadingScreen from './component/LoadingScreen';
import { DebugContext } from './context/DebugContext';
import { locationVersion } from './common/recoil/atom/LocationVersion';
import { sessionExpiredState } from './common/recoil/atom/SessionExpiredAtom';
import SessionExpired from './component/SessionExpired';

const OfflineScreen = lazy(() => import('./component/OfflineScreen'));
const EventViewer = lazy(() => import('./view/EventViewer'));
const ThemeWrapper = lazy(() => import('./wrapper/ThemeWrapper'));
const HTMLWrapper = lazy(() => import('./wrapper/HTMLWrapper'));

const Wrapper = (props) => {
  const { location } = props;
  const [currentPath, setCurrentPath] = useState(false);
  const [appOffline, setAppOffline] = useState(false);
  const debug = useContext(DebugContext);
  const setAction = useSetRecoilState(LogAction);
  const setCurrentApiVersion = useSetRecoilState(apiVersion);
  const setCurrentDateSelectionVersion =
    useSetRecoilState(dateSelectionVersion);
  const setCurrentLocationVersion = useSetRecoilState(locationVersion);
  const [queryParams, setQueryParams] = useRecoilState(queryString);
  const sessionExpired = useRecoilValue(sessionExpiredState);
  const DEBUG_STATUS = getConfigVariable('DEBUG');
  const [snapshots, setSnapshots] = useState([]);
  const [prevLocation, setPrevLocation] = useState();
  const consentChecked = useRef(false);

  const {
    data: heartbeatData,
    error: heartbeatError,
    isLoading: heartbeatLoading,
  } = fetchHeartbeat();

  useEffect(() => {
    setAction({
      context: 'startup',
      subject: 'app',
      object: 'wrapper',
      verb: 'loaded',
    });
    debug.setStatus(DEBUG_STATUS);
  }, [DEBUG_STATUS, debug, setAction]);

  useEffect(() => {
    const { pathname } = location;
    if (pathname !== currentPath) {
      setCurrentPath(pathname);

      // Fire Freshpaint event for each page transition
      const questionID = pathname.split('/').pop();
      if (questionID && EVENT_CODES[questionID]) {
        fpTrack(questionID);

        if (!consentChecked.current) {
          consentChecked.current = true;
          try {
            const consent = window.freshpaint?.get_consent?.();
            if (
              consent &&
              typeof consent === 'object' &&
              !Array.isArray(consent)
            ) {
              setAction({
                context: 'consent-check',
                subject: 'freshpaint',
                object: JSON.stringify(consent),
                verb: 'captured',
              });
            }
          } catch (e) {
            console.error('Freshpaint consent check error:', e);
          }
        }
      }
    }
  }, [location, currentPath, setCurrentPath, setAction]);

  useEffect(() => {
    const appOfflineStatus = checkHeartbeatStatus(
      heartbeatData,
      heartbeatError,
    );
    if (appOffline !== appOfflineStatus) setAppOffline(appOfflineStatus);
  }, [appOffline, heartbeatData, heartbeatError, heartbeatLoading]);

  // Call useRecoilSnapshot at the top level
  const snapshot = useRecoilSnapshot();
  const gotoSnapshot = useGotoRecoilSnapshot();

  // take a snapshot and append it upon every location change.
  useEffect(() => {
    // we cannot conditionally take a snapshot since it's IN A HOOK. we need to do this
    // weird prevLocation stuff instead.

    // we need to make sure we're snapshotting the user actually being on a page, or else
    // we'll just catch a garbage state and the backbutton will softlock.
    if (
      !sessionExpired &&
      prevLocation &&
      prevLocation !== location &&
      prevLocation.pathname.includes('/q')
    ) {
      setSnapshots((prevSnapshots) => {
        // One entry per screen: a new location object for the screen already on top
        // (restore, redirect, hash/query churn) keeps its first, settled snapshot.
        // Bare /q is a mid-redirect state, never stored.
        if (
          !/^\/q\/[^/]+$/.test(location.pathname) ||
          prevSnapshots[prevSnapshots.length - 1]?.path === location.pathname
        ) {
          return prevSnapshots;
        }
        return [...prevSnapshots, { snapshot, path: location.pathname }];
      });
    }
  }, [location, prevLocation, setSnapshots, snapshot, sessionExpired]);

  useEffect(() => {
    setPrevLocation(location);
  }, [location, setPrevLocation]);

  /**
   * Watch for query string changes, and pick up any query params that
   * are added at any point in the current state. This is setup so that
   * when the URL changes, and the query param disappears, the param
   * that was originally sent still is persisted.
   */
  useEffect(() => {
    let queryString = [...queryParams];

    window.location.hash
      .split('?')
      .slice(1)
      .forEach((itm) => {
        const split = itm.split('=');
        queryString[`${split[0].toLowerCase()}`] = split[1];
      });

    setQueryParams(queryString);
  }, [window.location.hash]);

  /**
   * Watch for the query param updates.
   */
  useEffect(() => {
    // Api version selection, toggles the appointment search version.
    if (queryParams['apiversion']) {
      setCurrentApiVersion(queryParams['apiversion']);
    }

    // Date selection version, toggles the event screen version for selecting
    // appointment dates.
    if (queryParams['dateselectionversion']) {
      setCurrentDateSelectionVersion(queryParams['dateselectionversion']);
    }

    if (queryParams['locationversion']) {
      setCurrentLocationVersion(queryParams['locationversion']);
    }
  }, [queryParams, setCurrentApiVersion, setCurrentDateSelectionVersion]);

  const backButtonFn = useCallback(() => {
    // No-op while expired: a snapshot restore would resurrect pre-expiry PHI/hold.
    if (sessionExpired) return;
    // No forward button: restore the latest entry for a different screen and drop everything after it.
    setSnapshots((prevSnapshots) => {
      let target = prevSnapshots.length - 1;
      while (target >= 0 && prevSnapshots[target].path === location.pathname) {
        target -= 1;
      }
      if (target < 0) return prevSnapshots;
      gotoSnapshot(prevSnapshots[target].snapshot);
      return prevSnapshots.slice(0, target + 1);
    });
  }, [setSnapshots, gotoSnapshot, sessionExpired, location.pathname]);

  // Drop stored snapshots on expiry so nothing can restore pre-expiry PHI/hold state.
  useEffect(() => {
    if (sessionExpired) setSnapshots([]);
  }, [sessionExpired]);

  const tstamp = new Date().getTime();

  /* Hide the back button on the confirmation screen (hitting back there breaks)
     and on the intro/landing screen (there's nothing to go back to — it's the
     first step, so the button is a dead no-op). */
  const shouldShowBackButton =
    !sessionExpired &&
    !location.pathname.endsWith(EVENTS.CONFIRMATION) &&
    !location.pathname.endsWith(EVENTS.INTRO_SCREEN);

  return (
    <Suspense fallback={<LoadingScreen />}>
      <HTMLWrapper>
        <ThemeWrapper
          shouldShowBackButton={shouldShowBackButton}
          backButtonFn={() => {
            backButtonFn();
          }}
        >
          {heartbeatLoading && <LoadingScreen />}
          {!heartbeatLoading && appOffline && <OfflineScreen />}
          {!heartbeatLoading && !appOffline && sessionExpired && (
            // Replaces the routed content so no previously-entered PHI stays
            // mounted (and thus visible/readable) behind the expiry message.
            <SessionExpired reason={sessionExpired} />
          )}
          {!heartbeatLoading && !appOffline && !sessionExpired && (
            <Switch>
              <Route
                exact
                path="/q"
                render={(props) => <EventViewer {...props} />}
              />
              {/* :questionID => props.match.questionID is now the string after the /q/ in
              the EventViewer instance spawned here. */}
              <Route
                exact
                path="/q/:questionID"
                render={(props) => <EventViewer {...props} />}
              />
              <Redirect
                from="/"
                to="/q"
              />
            </Switch>
          )}
        </ThemeWrapper>
      </HTMLWrapper>
    </Suspense>
  );
};

Wrapper.propTypes = {
  location: PropTypes.object.isRequired,
};

export default withRouter(Wrapper);
